<template>
    <div class="py-6 px-4 sm:p-6 lg:pb-8">
        
        <h2 class="text-lg leading-6 font-medium text-gray-900">Qualifications</h2>
        <p class="mt-1 text-sm text-gray-500">
            List your qualifications and achievements.
        </p>
        


        <tti-button v-if="!isAddingNewQualification && qualifications.length === 0" @click="isAddingNewQualification = true" class="mt-6">Add Qualification</tti-button>
        <tti-button v-else-if="!isAddingNewQualification && qualifications.length > 0" @click="isAddingNewQualification = true" size="xsmall" class="mt-6">Add Another Qualification</tti-button>

        <div v-if="isAddingNewQualification" class="bg-gray-50 overflow-hidden rounded-lg mt-6">
            <div class="px-4 py-5 sm:p-6">

                <form @submit.prevent="onAddQualtification" class="space-y-6">
                    
                    <div class="grid grid-cols-12">
                        <div class="col-span-12 sm:col-span-9">
                            <label for="subject" class="block text-sm font-medium text-gray-700">Subject</label>
                            <input v-model.trim="newQualificationSubject" @input="$v.newQualificationSubject.$touch()" type="text" name="subject" id="subject" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400" placeholder="e.g. Microbiology & Zoology, Piano">
                            <p v-if="$v.newQualificationSubject.$dirty && !$v.newQualificationSubject.required" class="mt-2 text-sm text-red-600">Please enter a subject.</p>
                        </div>
                    </div>

                    <div class="grid grid-cols-12">
                        <div class="col-span-12 sm:col-span-6">
                            <label for="level" class="block text-sm font-medium text-gray-700">Level</label>
                            <input v-model.trim="newQualificationLevel" type="text" name="level" id="level" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400" placeholder="e.g. A-Level, BSc, Grade 8">
                        </div>
                    </div>

                    <div class="grid grid-cols-12">
                        <div class="col-span-12 sm:col-span-6">
                            <label for="grade" class="block text-sm font-medium text-gray-700">Grade</label>
                            <input v-model.trim="newQualificationGrade" type="text" name="grade" id="grade" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400" placeholder="e.g. 1st Class Honours, Distinction">
                        </div>
                    </div>

                    <div class="grid grid-cols-12">
                        <div class="col-span-12 sm:col-span-9">
                            <label for="institution" class="block text-sm font-medium text-gray-700">Institution</label>
                            <input v-model.trim="newQualificationInstitution" type="text" name="institution" id="institution" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400" placeholder="e.g. University of Oxford">
                        </div>
                    </div>

                    <div class="grid grid-cols-12">
                        <div class="col-span-6 sm:col-span-3">
                            <label for="year" class="block text-sm font-medium text-gray-700">Year of Completion</label>
                            <input v-model.number="newQualificationYear" type="number" min="1900" max="2099" name="year" id="year" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-light-blue-500 focus:border-light-blue-500 sm:text-sm placeholder-gray-400" placeholder="e.g. 2008">
                        </div>
                    </div>


                    <div class="mt-6">
                        <div class="space-x-2">
                            <tti-button type="submit" :loading="savingQualification" :disabled="savingQualification">Add Qualification</tti-button>
                            <tti-button @click="onCancelAddQualification" theme="outline" :disabled="savingQualification">Cancel</tti-button>
                        </div>

                        <div v-if="$v.$anyError" class="mt-3 text-sm text-red-600">Uh oh, it looks like some errors need fixing first.</div>
                    </div>
                </form>

            </div>
        </div>

        

        <div class="mt-6">
            <p v-if="qualifications.length > 1" class="mb-4 font-semibold text-gray-500">
                The first qualification listed will be shown when you appear in search results.
            </p>

            <draggable v-model="qualifications" @end="onChangeSortOrder" handle=".handle" ghost-class="bg-gray-100" class="flex flex-col space-y-8">
                <div v-for="(qualification, index) in qualifications" :key="qualification.Id" class="flex space-x-3">
                    
                    <button type="button" @click="onDeleteQualification(qualification, index)" class="pt-0.5 flex-shrink-0 flex items-start">
                        <heroicon icon="trash" class="w-5 h-5 text-gray-400 hover:text-gray-500"></heroicon>
                    </button>

                    <div class="w-full">
                        <h4 class="font-semibold">
                            {{ qualification.Level }}
                            {{ qualification.Subject }}
                            <span class="ml-1.5 font-normal text-gray-500">{{ qualification.Grade }}</span>
                        </h4>
                        <p v-if="qualification.Institution || qualification.Year">
                            {{ [qualification.Institution, qualification.Year].filter(Boolean).join(', ') }}
                        </p>
                        <p v-if="qualification.proofPending" class="mt-1 text-sm text-gray-500">
                            Thank you for your submission.
                        </p>
                        <span v-else-if="qualification.VerifiedWhen" class="inline-flex mt-1 items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 space-x-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.2.1 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z" fill="currentColor" /></svg>
                            <span>Verified</span>
                        </span>
                        <tti-button
                            v-else
                            @click="initialiseProofUpload(qualification)"
                            size="xsmall"
                            class="mt-2"
                            :disabled="qualification.isUploadingProof"
                            :loading="qualification.isUploadingProof"
                        >
                            Upload Proof
                        </tti-button>
                    </div>
                    
                    <heroicon v-if="qualifications.length > 1" icon="menu-alt-4" class="mt-0.5 w-6 h-6 flex-shrink-0 text-gray-400 hover:text-gray-500 handle"></heroicon>
                    
                </div>
            </draggable>


            <input
                ref="uploadQualificationInput"
                type="file"
                class="hidden"
                @change="uploadQualification"
            />

        </div>

    </div>
</template>
<script>
import cloneDeep from 'lodash.clonedeep'

import dayjs from 'dayjs'
import { required } from 'vuelidate/lib/validators'

import TutorService from '../services/TutorServices'
const tutorService = new TutorService();

export default {
    components: {
        'heroicon': () => import('./Heroicon'),
        'draggable': () => import('vuedraggable'),
        'tti-button': () => import('./TailwindButton')
    },
    data() {
        return {
            dbsCheckDate: null,
            isAddingNewQualification: false,
            newQualificationSubject: null,
            newQualificationLevel: null,
            newQualificationGrade: null,
            newQualificationInstitution: null,
            newQualificationYear: null,
            qualifications: [],
            savingQualification: false
        }
    },
    validations: {
        newQualificationSubject: {
            required
        }
    },
    methods: {
        async onDbsCheckDateBlur() {
            if (this.dbsCheckDate === this.$store.state.user.DbsCheckDate)
                return;

            await this.$store.dispatch('updateUser', { DbsCheckDate: this.dbsCheckDate });
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
        },
        async onAddQualtification() {
            this.$v.$touch();
            if (this.$v.$invalid)
                return;

            var newQualification = {
                Subject: this.newQualificationSubject,
                Level: this.newQualificationLevel,
                Grade: this.newQualificationGrade,
                Institution: this.newQualificationInstitution,
                Year: this.newQualificationYear
            };


            this.savingQualification = true;
            var response = await tutorService.addQualification(newQualification);            
            this.savingQualification = false;

            newQualification.Id = response.data.Id;
            this.qualifications.push(newQualification);

            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });

            this.resetAddQualificationForm();
            this.isAddingNewQualification = false;
        },
        onCancelAddQualification() {
            this.resetAddQualificationForm();
            this.isAddingNewQualification = false;
        },
        async onChangeSortOrder() {
            await tutorService.sortQualifications(this.qualifications.map(x => x.Id));
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
            //console.log(JSON.stringify(this.qualifications));
        },
        async onDeleteQualification(qualification, index) {
            await tutorService.deleteQualification(qualification.Id);
            this.$delete(this.qualifications, index);
            this.$store.commit('showModal', { icon: 'check-circle', title: 'Saved.' });
        },
        resetAddQualificationForm() {
            this.newQualificationSubject = null;
            this.newQualificationLevel = null;
            this.newQualificationGrade = null;
            this.newQualificationInstitution = null;
            this.newQualificationYear = null;
            this.$nextTick(() => { this.$v.$reset(); });
        },
        initialiseProofUpload(qualification){
            this.$refs.uploadQualificationInput.dataset.qualificationId = qualification.Id
            this.$refs.uploadQualificationInput.click()
        },
        async uploadQualification($event) {
            const file = $event.target.files[0]
            if (!file) return

            const qualificationId = $event.target.dataset.qualificationId
            this.qualifications.find(x => x.Id === qualificationId).isUploadingProof = true

            try {
                await tutorService.uploadQualification(
                    file
                )
            }
            catch {
                alert('Something went wrong.')
                return
            }
            finally {
                this.qualifications.find(x => x.Id === qualificationId).isUploadingProof = false
            }

            this.qualifications.find(x => x.Id === qualificationId).proofPending = true
        }
    },
    mounted() {
        if (this.$store.state.user.DbsCheckDate)
            this.dbsCheckDate = dayjs(this.$store.state.user.DbsCheckDate).format('YYYY-MM-DD');

        const qs = cloneDeep(this.$store.state.user.Qualifications)
        qs.forEach(q => {
            q.proofPending = false
            q.isUploadingProof = false
        })
        
        this.qualifications = qs
    }
}
</script>